import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import { useTranslation } from 'react-i18next';
import useLab from '../../hooks/useLab';
import { setLabHospital } from '../../utils/labHospital';
import { adminService } from '../../api/services';

const Card = ({ icon, title, children }) => (
  <Paper
    variant="outlined"
    sx={{ p: { xs: 3, sm: 5 }, maxWidth: 560, mx: 'auto', mt: 4, textAlign: 'center', borderRadius: 4 }}
  >
    <Box
      sx={{
        width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
        display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'text.secondary',
      }}
    >
      {icon}
    </Box>
    <Typography variant="h5" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    {children}
  </Paper>
);

/** Platform admin: choose which hospital's lab to open (06 §1.4). */
const HospitalPicker = () => {
  const { t } = useTranslation();
  const [hospitals, setHospitals] = useState([]);
  useEffect(() => {
    adminService
      ?.getHospitals?.()
      .then((res) => setHospitals(res.data?.results || res.data || []))
      .catch(() => setHospitals([]));
  }, []);
  return (
    <Card icon={<ScienceRoundedIcon sx={{ fontSize: 32 }} />} title={t('lab.common.chooseHospital', 'Choose a hospital')}>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('lab.common.chooseHospitalBody', 'Lab pages act on one hospital at a time. Pick the one to open.')}
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        label={t('lab.common.hospital', 'Hospital')}
        value=""
        onChange={(e) => {
          setLabHospital(e.target.value);
          window.location.reload();
        }}
      >
        {hospitals.map((h) => (
          <MenuItem key={h.id} value={h.id}>
            {h.name}
          </MenuItem>
        ))}
      </TextField>
    </Card>
  );
};

/**
 * Renders `children` only when the user holds `permission` (a key, or an
 * array meaning ANY of them). Otherwise an in-page card — never a redirect.
 */
const LabGuard = ({ permission, children }) => {
  const { t } = useTranslation();
  const lab = useLab();

  if (lab.needsHospital) return <HospitalPicker />;

  if (!lab.enabled) {
    return (
      <Card icon={<ScienceRoundedIcon sx={{ fontSize: 32 }} />} title={t('lab.common.labNotEnabledTitle', 'Laboratory not enabled')}>
        <Typography color="text.secondary">
          {lab.role === 'lab_staff'
            ? t('lab.common.accountInactive', 'Your lab account is not active. Ask your lab manager.')
            : `The laboratory is not enabled for ${lab.hospitalName || 'this hospital'}. A platform administrator can enable it.`}
        </Typography>
        {lab.isAdmin && (
          <Button variant="contained" sx={{ mt: 3 }} href="/admin/labs">
            {t('lab.common.openLaboratories', 'Open Laboratories')}
          </Button>
        )}
      </Card>
    );
  }

  if (permission && !lab.can(permission)) {
    return (
      <Card icon={<LockRoundedIcon sx={{ fontSize: 32 }} />} title={t('lab.common.notAvailable', 'Not available for your role')}>
        <Typography color="text.secondary">
          The <strong>{lab.roleLabel}</strong> role does not have access to this area. Ask an administrator if
          you need it.
        </Typography>
      </Card>
    );
  }

  return children;
};

export default LabGuard;
